import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { playTTS } from "@/lib/tts";
import { useAuth } from "@/context/AuthContext";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Volume2, CheckCircle2, XCircle, MessageSquareText, PlayCircle, Mic, Square, Loader2, X } from "lucide-react";

function buildBlank(sentence, word) {
  const regex = new RegExp(`\\b${word}\\b`, "i");
  return sentence.replace(regex, "_____");
}

function boldWord(sentence, word) {
  if (!sentence || !word) return sentence;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = sentence.split(new RegExp(`(\\b${escaped}\\b)`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === word.toLowerCase() ? (
      <strong key={i} className="font-extrabold text-foreground">{part}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Review() {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [fillAnswer, setFillAnswer] = useState("");
  const [fillResult, setFillResult] = useState(null);
  const [pronunciationResult, setPronunciationResult] = useState(null);
  const [retellText, setRetellText] = useState("");
  const [retellResult, setRetellResult] = useState(null);
  const [inlinePlayer, setInlinePlayer] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isSupported: speechSupported, isListening, start: startListening, stop: stopListening } = useSpeechRecognition("en-US");

  const { data: queue = [], isLoading } = useQuery({
    queryKey: ["review-queue"],
    queryFn: async () => (await api.get("/review/queue")).data,
  });

  const answerMutation = useMutation({
    mutationFn: ({ wordId, rating, cardType }) =>
      api.post(`/review/${wordId}/answer`, { rating, card_type: cardType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats-today"] });
    },
  });

  const retellMutation = useMutation({
    mutationFn: () => api.post(`/review/${queue[index].word_id}/retell-check`, { retell_text: retellText }),
    onSuccess: ({ data }) => {
      setRetellResult(data);
      setRevealed(true);
    },
  });

  const card = queue[index];

  useEffect(() => {
    setRevealed(false);
    setFillAnswer("");
    setFillResult(null);
    setPronunciationResult(null);
    setRetellText("");
    setRetellResult(null);
    setInlinePlayer(null);
  }, [index]);

  const speak = (text) => {
    playTTS(text, user?.settings);
  };

  const checkFillAnswer = () => {
    const isCorrect = fillAnswer.trim().toLowerCase() === card.word.trim().toLowerCase();
    setFillResult({ isCorrect });
    setRevealed(true);
  };

  const handlePronunciationRecord = () => {
    if (isListening) {
      stopListening();
      return;
    }
    startListening((transcript) => {
      const isCorrect = transcript.trim().toLowerCase().includes(card.word.trim().toLowerCase());
      setPronunciationResult({ isCorrect, transcript });
      setRevealed(true);
    });
  };

  const handleAnswer = (rating) => {
    answerMutation.mutate({ wordId: card.word_id, rating, cardType: card.card_type });
    setIndex((i) => i + 1);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-full text-muted-foreground">Загрузка карточек...</div>;
  }

  if (!card) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-4" data-testid="review-complete-state">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="font-heading text-2xl font-bold">
          {queue.length === 0 ? "На сегодня карточек нет" : "Повторение завершено!"}
        </h2>
        <p className="text-muted-foreground max-w-sm">
          Отличная работа. Хотите закрепить слова в разговоре с AI?
        </p>
        <Button onClick={() => navigate("/practice")} data-testid="review-go-to-practice-button">
          <MessageSquareText className="w-4 h-4 mr-1" /> Перейти к AI практике
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col items-center justify-center px-4 py-6 overflow-y-auto" data-testid="review-page">
      <div className="w-full max-w-lg space-y-2 mb-8">
        <Progress value={(index / queue.length) * 100} className="h-1.5" data-testid="review-progress-bar" />
        <p className="text-sm text-muted-foreground text-center">{index + 1} / {queue.length}</p>
      </div>

      <div className="w-full max-w-lg bg-card border border-border rounded-2xl p-8 shadow-sm space-y-6" data-testid="review-card">
        {card.card_type === "recall" && (
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Вспомните перевод</p>
            <h2 className="font-heading text-4xl font-extrabold" data-testid="review-prompt-word">{card.word}</h2>
            <button onClick={() => speak(card.word)} className="text-muted-foreground hover:text-primary mx-auto block">
              <Volume2 className="w-5 h-5" />
            </button>
          </div>
        )}

        {card.card_type === "recognize" && (
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Как это будет на английском?</p>
            <h2 className="font-heading text-4xl font-extrabold" data-testid="review-prompt-word">{card.translation}</h2>
          </div>
        )}

        {card.card_type === "fill_blank" && card.context && (
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Заполните пропуск</p>
            <p className="text-xl font-medium" data-testid="review-prompt-word">{buildBlank(card.context.sentence, card.word)}</p>
          </div>
        )}

        {card.card_type === "listening" && (
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Прослушайте и вспомните слово</p>
            <Button variant="outline" size="lg" onClick={() => speak(card.word)} data-testid="review-play-audio-button">
              <Volume2 className="w-5 h-5 mr-2" /> Прослушать
            </Button>
          </div>
        )}

        {card.card_type === "pronunciation" && (
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Произнесите слово вслух</p>
            <h2 className="font-heading text-4xl font-extrabold" data-testid="review-prompt-word">{card.word}</h2>
            <button onClick={() => speak(card.word)} className="text-muted-foreground hover:text-primary mx-auto block">
              <Volume2 className="w-5 h-5" />
            </button>
          </div>
        )}

        {card.card_type === "retell" && card.context && (
          <div className="text-center space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Перескажите своими словами</p>
            <p className="text-lg italic" data-testid="review-prompt-word">"{card.context.sentence}"</p>
          </div>
        )}

        {!revealed ? (
          card.card_type === "fill_blank" ? (
            <form onSubmit={(e) => { e.preventDefault(); checkFillAnswer(); }} className="space-y-3">
              <Input
                autoFocus
                value={fillAnswer}
                onChange={(e) => setFillAnswer(e.target.value)}
                placeholder="Впишите слово..."
                data-testid="review-fill-blank-input"
              />
              <Button type="submit" className="w-full" size="lg" disabled={!fillAnswer.trim()} data-testid="review-check-answer-button">
                Проверить
              </Button>
            </form>
          ) : card.card_type === "pronunciation" && speechSupported ? (
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={handlePronunciationRecord}
                data-testid="review-pronunciation-mic-button"
                className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200 ${
                  isListening ? "bg-red-500 animate-pulse" : "bg-primary"
                } text-white`}
              >
                {isListening ? <Square className="w-5 h-5" /> : <Mic className="w-6 h-6" />}
              </button>
              <p className="text-xs text-muted-foreground">{isListening ? "Слушаю..." : "Нажмите и произнесите слово"}</p>
              <button
                onClick={() => setRevealed(true)}
                data-testid="review-pronunciation-skip-button"
                className="text-xs text-muted-foreground hover:text-primary underline mt-1"
              >
                Не удаётся распознать — показать ответ
              </button>
            </div>
          ) : card.card_type === "retell" ? (
            <form onSubmit={(e) => { e.preventDefault(); retellMutation.mutate(); }} className="space-y-3">
              <Textarea
                autoFocus
                value={retellText}
                onChange={(e) => setRetellText(e.target.value)}
                placeholder="Перескажите предложение своими словами..."
                data-testid="review-retell-input"
                rows={3}
              />
              <Button type="submit" className="w-full" size="lg" disabled={!retellText.trim() || retellMutation.isPending} data-testid="review-retell-submit-button">
                {retellMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Проверить"}
              </Button>
            </form>
          ) : (
            <Button className="w-full" size="lg" onClick={() => setRevealed(true)} data-testid="review-reveal-button">
              Показать ответ
            </Button>
          )
        ) : (
          <>
            {card.card_type === "fill_blank" && fillResult && (
              <div className="text-center space-y-1" data-testid="review-fill-blank-result">
                <div className={`flex items-center justify-center gap-2 font-medium ${fillResult.isCorrect ? "text-emerald-600" : "text-red-600"}`}>
                  {fillResult.isCorrect ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                  {fillResult.isCorrect ? "Правильно!" : "Неверно"}
                </div>
                {!fillResult.isCorrect && (
                  <p className="text-sm text-muted-foreground">
                    Ваш ответ: <span className="line-through">{fillAnswer || "—"}</span> · Правильный ответ:{" "}
                    <span className="font-medium text-foreground">{card.word}</span>
                  </p>
                )}
              </div>
            )}
            {card.card_type === "pronunciation" && pronunciationResult && (
              <div
                className={`flex items-center justify-center gap-2 font-medium text-center ${pronunciationResult.isCorrect ? "text-emerald-600" : "text-red-600"}`}
                data-testid="review-pronunciation-result"
              >
                {pronunciationResult.isCorrect ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
                {pronunciationResult.isCorrect ? "Отлично! Произношение распознано верно" : `Распознано: "${pronunciationResult.transcript}" — попробуйте ещё раз`}
              </div>
            )}
            {card.card_type === "retell" && retellResult && (
              <div className="text-center space-y-1" data-testid="review-retell-result">
                <div className={`flex items-center justify-center gap-2 font-medium ${retellResult.is_correct ? "text-emerald-600" : "text-orange-600"}`}>
                  {retellResult.is_correct ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                  {retellResult.is_correct ? "Хороший пересказ!" : "Можно лучше"}
                </div>
                <p className="text-sm text-muted-foreground">{retellResult.feedback}</p>
              </div>
            )}
            <div className="text-center space-y-2 py-3 border-y border-border" data-testid="review-answer">
              {!["recall", "pronunciation"].includes(card.card_type) && (
                <div className="flex items-center justify-center gap-2">
                  <p className="font-heading text-2xl font-bold">{card.word}</p>
                  <button onClick={() => speak(card.word)} data-testid="review-answer-speak-button" className="text-muted-foreground hover:text-primary transition-colors">
                    <Volume2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-muted-foreground">
                {card.transcription} · <span className="font-bold text-foreground">{card.translation}</span>
              </p>
              <p className="text-sm text-muted-foreground">{card.definition}</p>
              {card.context && (
                card.context.youtube_id ? (
                  <button
                    onClick={() => setInlinePlayer({ youtubeId: card.context.youtube_id, timestamp: card.context.timestamp || 0 })}
                    data-testid="review-context-timeline"
                    className="flex items-center gap-1.5 text-sm italic text-primary hover:underline mt-2 mx-auto"
                  >
                    <PlayCircle className="w-4 h-4 shrink-0" />
                    <span>"{boldWord(card.context.sentence, card.word)}"</span>
                    <span className="shrink-0 not-italic">· {formatTime(card.context.timestamp || 0)}</span>
                  </button>
                ) : card.context.video_id ? (
                  <button
                    onClick={() => navigate(`/player/${card.context.video_id}?t=${Math.floor(card.context.timestamp || 0)}`)}
                    data-testid="review-context-timeline"
                    className="flex items-center gap-1.5 text-sm italic text-primary hover:underline mt-2 mx-auto"
                  >
                    <PlayCircle className="w-4 h-4 shrink-0" />
                    <span>"{boldWord(card.context.sentence, card.word)}"</span>
                    <span className="shrink-0 not-italic">· {formatTime(card.context.timestamp || 0)}</span>
                  </button>
                ) : (
                  <p className="text-sm italic mt-2">"{boldWord(card.context.sentence, card.word)}"</p>
                )
              )}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <Button onClick={() => handleAnswer("again")} className="bg-red-500 hover:bg-red-600 text-white" data-testid="review-answer-again">Again</Button>
              <Button onClick={() => handleAnswer("hard")} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="review-answer-hard">Hard</Button>
              <Button onClick={() => handleAnswer("good")} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="review-answer-good">Good</Button>
              <Button onClick={() => handleAnswer("easy")} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="review-answer-easy">Easy</Button>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {inlinePlayer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            data-testid="review-inline-player"
            onClick={() => setInlinePlayer(null)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              className="w-full max-w-3xl bg-black rounded-2xl overflow-hidden shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setInlinePlayer(null)}
                data-testid="review-inline-player-close-button"
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="aspect-video">
                <iframe
                  key={`${inlinePlayer.youtubeId}-${inlinePlayer.timestamp}`}
                  src={`https://www.youtube.com/embed/${inlinePlayer.youtubeId}?start=${Math.floor(inlinePlayer.timestamp)}&autoplay=1&rel=0`}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title="context-video"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
